import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Heart } from 'lucide-react';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { LikeVisibility } from '../../types';
import styles from './LikePrivacyPrompt.module.css';

interface LikePrivacyPromptProps {
  isOpen: boolean;
  onClose: () => void;
  onChoice: (visibility: LikeVisibility) => void;
}

const options = [
  {
    value: 'public' as const,
    icon: Eye,
    title: 'Public',
    description: 'Your name will appear alongside your likes so others can see what you enjoyed.',
    accentClass: 'accent-mint',
    hoverClass: 'option-public',
  },
  {
    value: 'anonymous' as const,
    icon: EyeOff,
    title: 'Anonymous',
    description: 'Your likes stay private. Others will see the like count but not who liked.',
    accentClass: 'accent-indigo',
    hoverClass: 'option-anonymous',
  },
];

export function LikePrivacyPrompt({ isOpen, onClose, onChoice }: LikePrivacyPromptProps) {
  const [saving, setSaving] = useState(false);

  const handleChoice = async (visibility: LikeVisibility) => {
    setSaving(true);
    try {
      await onChoice(visibility);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <div className={styles['content']}>
        {/* Header illustration */}
        <div className={styles['header']}>
          <div className={styles['heart-icon-wrap']}>
            <Heart className={styles['icon-heart']} strokeWidth={1.8} color="var(--clay-rose)" />
          </div>
          <h3 className={styles['heading']}>
            How should your likes appear?
          </h3>
          <p className={styles['subheading']}>
            Choose whether others can see your name when you like a photo. You can change this anytime in your settings.
          </p>
        </div>

        {/* Options */}
        <div className={styles['options-grid']}>
          {options.map(({ value, icon: Icon, title, description, accentClass, hoverClass }) => (
            <motion.button
              key={value}
              onClick={() => handleChoice(value)}
              disabled={saving}
              whileHover={{ y: -2, scale: 1.01 }}
              whileTap={{ scale: 0.98 }}
              className={[
                styles['option-card'],
                styles[hoverClass],
                styles['card-shadow'],
                saving ? styles['disabled'] : '',
              ].filter(Boolean).join(' ')}
            >
              <div className={styles['option-content']}>
                <div className={[styles['option-icon-wrap'], styles[accentClass]].join(' ')}>
                  <Icon size={20} strokeWidth={1.8} />
                </div>
                <div className={styles['option-body']}>
                  <span className={styles['option-title']}>
                    {title}
                  </span>
                  <p className={styles['option-description']}>
                    {description}
                  </p>
                </div>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Dismiss */}
        <div className={styles['footer']}>
          <Button variant="ghost" size="sm" onClick={onClose} className={styles['btn-full-width']}>
            Decide later
          </Button>
        </div>
      </div>
    </Modal>
  );
}
